<script>
import BaseButtonV2 from '@/components/lavender/v2/BaseButton.vue'
import BaseInputV2 from '@/components/lavender/v2/BaseInput.vue'
import {
  checkPhoneNumber,
  findCountryDataByCountryCallingCode,
} from '@/utils/country-data'

const INQUIRY_ABOUT_OPTIONS = {
  in_store: '2',
  online: '1',
}

export default {
  name: 'SupportCallbackDialog',
  components: {
    BaseInputV2,
    BaseButtonV2,
  },
  props: {
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      countryCode: '971',
      phoneNumber: '',
      inquiryAbout: '',
      errorMessages: {
        phoneNumber: '',
      },
    }
  },
  computed: {
    inquiryAboutOptions() {
      return [
        {
          label: this.$t(
            'support_widget_request_callback_inquiry_about_in_store'
          ),
          value: INQUIRY_ABOUT_OPTIONS.in_store,
        },
        {
          label: this.$t(
            'support_widget_request_callback_inquiry_about_online'
          ),
          value: INQUIRY_ABOUT_OPTIONS.online,
        },
      ]
    },
    maxLength() {
      return findCountryDataByCountryCallingCode(this.countryCode)
        .numberMaxLength
    },
  },
  methods: {
    validateLength(value, maxLength) {
      if (value.length >= maxLength) {
        return false
      }

      return true
    },
    isNumber(evt) {
      evt = evt || window.event

      const value = evt.target.value

      if (!this.validateLength(value, this.maxLength)) {
        evt.stopPropagation()
        evt.preventDefault()

        return false
      }

      const charCode = evt.which ? evt.which : evt.keyCode

      if (
        (charCode < 1632 || charCode > 1641) && // To allow arabic numbers
        charCode > 31 &&
        (charCode < 48 || charCode > 57) &&
        charCode !== 46
      ) {
        evt.stopPropagation()
        evt.preventDefault()

        return false
      }

      return true
    },
    validatePhoneNumber() {
      const errorMessage = checkPhoneNumber(this.countryCode, this.phoneNumber)

      if (errorMessage) {
        this.errorMessages.phoneNumber = this.$t(errorMessage)

        return false
      }

      this.errorMessages.phoneNumber = ''

      return true
    },
    blurPhoneNumberInput() {
      this.validatePhoneNumber()
    },
    handleSubmit() {
      if (!this.validatePhoneNumber()) {
        return
      }

      this.$emit('submit', {
        countryCode: this.countryCode,
        phoneNumber: this.phoneNumber,
        inquiryAbout: this.inquiryAbout,
      })

      this.clearData()
    },
    handleCancel() {
      this.$emit('cancel')

      this.clearData()
    },
    clearData() {
      this.phoneNumber = ''
      this.inquiryAbout = ''
      this.errorMessages.phoneNumber = ''
    },
  },
}
</script>

<template>
  <div class="support-callback-dialog is-lavender-design">
    <div class="support-callback-dialog__head">
      <div class="support-callback-dialog__head-title">
        {{ $t('sidebar_support_widget_request_callback_title') }}
      </div>
      <div class="support-callback-dialog__head-description">
        {{ $t('sidebar_support_widget_request_callback_description') }}
      </div>
    </div>
    <div class="support-callback-dialog__body">
      <div class="support-callback-dialog__body-item__contact">
        <label class="support-callback-dialog__body-item__contact-label">
          {{ $t('sidebar_support_widget_request_callback_phone_number_label') }}
        </label>
        <BaseInputV2
          v-model="phoneNumber"
          type="tel"
          pattern="[0-9]*"
          inputmode="numeric"
          :placeholder="
            $t(
              'sidebar_support_widget_request_callback_phone_number_placeholder'
            )
          "
          :disabled="submitting"
          class="support-callback-dialog__body-item__contact-input"
          @keypress.native.stop="isNumber"
          @blur="blurPhoneNumberInput"
        >
          <template v-slot:prepend>
            <svg-icon name="flag-ae-square" width="24px" height="24px" />
          </template>
        </BaseInputV2>
        <Transition mode="out-in" name="fade">
          <div
            v-show="errorMessages.phoneNumber"
            class="support-callback-dialog__body-item__contact-error"
          >
            {{ errorMessages.phoneNumber }}
          </div>
        </Transition>
      </div>
      <div class="support-callback-dialog__body-item__inquiry-about">
        <label class="support-callback-dialog__body-item__inquiry-about-label">
          {{
            $t('sidebar_support_widget_request_callback_inquiry_about_label')
          }}
        </label>
        <el-radio-group v-model="inquiryAbout" :disabled="submitting">
          <el-radio
            v-for="option in inquiryAboutOptions"
            :key="option.value"
            :label="option.value"
          >
            {{ option.label }}
          </el-radio>
        </el-radio-group>
      </div>
    </div>
    <div class="support-callback-dialog__footer">
      <BaseButtonV2
        type="primary"
        size="medium"
        :disabled="submitting"
        :loading="submitting"
        class="support-callback-dialog__footer-submit"
        @click="handleSubmit"
      >
        {{ $t('sidebar_support_widget_request_callback_submit') }}
      </BaseButtonV2>
      <BaseButtonV2
        type="tertiary"
        size="medium"
        :disabled="submitting"
        class="support-callback-dialog__footer-cancel"
        @click="handleCancel"
      >
        {{ $t('sidebar_support_widget_request_callback_cancel') }}
      </BaseButtonV2>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/lavender/_typography.scss';

.support-callback-dialog {
  &__head {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__head-title {
    @extend .tamara-english-heading-5;

    color: var(--tamara-color-content-high);
  }

  &__head-description {
    @extend .tamara-english-regular-3;

    color: var(--tamara-color-content-mid);
  }

  &__body {
    display: flex;
    flex-direction: column;
    margin-top: 24px;
  }

  &__body-item {
    &__contact-error {
      @extend .tamara-english-regular-2;

      color: var(--tamara-color-error);
    }

    &__contact-label {
      @extend .tamara-english-regular-2;

      color: var(--tamara-color-content-mid);

      &::after {
        color: var(--tamara-color-brand-700);
        content: '*';
      }
    }

    &__contact-input {
      max-width: 320px;
    }

    &__contact {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &__inquiry-about {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-top: 30px;
    }

    &__inquiry-about-label {
      @extend .tamara-english-medium-2;

      color: var(--tamara-color-content-high);
    }
  }

  &__footer {
    display: flex;
    flex-direction: row;
    gap: 16px;
    justify-content: flex-start;
    margin-top: 40px;

    &-submit {
      padding-inline: 48px;
    }

    &-cancel {
      padding-inline: 24px;
    }
  }
}
</style>
